import {
    OpcodeJsonShape,
    JsonOpcodeStep,
    LLMStep,
    RAGStep,
    APIStep,
    ChooseStep,
    SubflowStep,
    ToolStep,
    ParallelStep,
    CallStep
} from 'builders/builderOpcode/jsonMode/types';

export type ValidationResult = {
    isValid: boolean;
    message: string;
};

// Registries the editor can resolve external refs against. Provide what you
// have — any omitted set is skipped (we never flag a ref the editor cannot
// see). A literal id like "kc_42" matches; an OEL expression (e.g.
// "@input.collection") is left alone since we cannot resolve it statically.
export type AvailableRefs = {
    instructionUniqueNames?: Set<string>;
    apiIds?: Set<string>;
    knowledgeCollectionIds?: Set<string>;
    mcpConnectionIds?: Set<string>;
};

const isOelExpr = (v: unknown): boolean =>
    typeof v === 'string' && v.trim().startsWith('@');

class V2OpCodeBuilderHandler {
    validateOpcode(opcode: OpcodeJsonShape, availableRefs?: AvailableRefs): ValidationResult {
        const errors: string[] = [];

        // V2+ canonical: title; legacy fallback: name.
        const titleish = opcode.title || opcode.name || '';
        if (!titleish.trim()) {
            errors.push('Opcode title is required.');
        }

        if (!opcode.steps || opcode.steps.length === 0) {
            errors.push('At least one step is required.');
        }

        // Validate each step
        opcode.steps.forEach((step, index) => {
            const result = this.validateStep(step);
            if (!result.isValid) {
                errors.push(`Step ${index + 1} (${step.id}): ${result.message}`);
            }
        });

        // Validate definitions
        if (opcode.definitions) {
            opcode.definitions.forEach((def, index) => {
                const result = this.validateStep(def);
                if (!result.isValid) {
                    errors.push(`Definition ${index + 1} (${def.id}): ${result.message}`);
                }
            });
        }

        // Validate flows
        if (opcode.flows) {
            opcode.flows.forEach((flow, index) => {
                if (!flow.id || flow.id.trim().length === 0) {
                    errors.push(`Flow ${index + 1}: flow ID is required.`);
                }
                flow.steps.forEach((step, stepIndex) => {
                    const result = this.validateStep(step);
                    if (!result.isValid) {
                        errors.push(`Flow "${flow.id}" step ${stepIndex + 1} (${step.id}): ${result.message}`);
                    }
                });
            });
        }

        // Validate cross-references
        const refResult = this.validateReferences(opcode);
        if (!refResult.isValid) {
            errors.push(refResult.message);
        }

        // Validate external resource refs (instruction/api/KC/MCP)
        if (availableRefs) {
            const extResult = this.validateExternalReferences(opcode, availableRefs);
            if (!extResult.isValid) {
                errors.push(extResult.message);
            }
        }

        // Validate unique step IDs
        const idResult = this.validateUniqueIds(opcode);
        if (!idResult.isValid) {
            errors.push(idResult.message);
        }

        const fallbackResult = this.validateFallbackStepRefs(opcode);
        if (!fallbackResult.isValid) {
            errors.push(fallbackResult.message);
        }

        return {
            isValid: errors.length === 0,
            message: errors.length > 0 ? errors.join(' ') : 'Valid opcode.'
        };
    }

    validateStep(step: JsonOpcodeStep): ValidationResult {
        if (!step.id || step.id.trim().length === 0) {
            return {
 isValid: false,
message: 'Step ID is required.'
};
        }

        const commonErrors = this.validateStepCommonFields(step);
        let kindResult: ValidationResult;

        switch (step.kind) {
            case 'llm':
                kindResult = this.validateLLMStep(step as LLMStep);
                break;
            case 'rag':
                kindResult = this.validateRAGStep(step as RAGStep);
                break;
            case 'api':
                kindResult = this.validateAPIStep(step as APIStep);
                break;
            case 'memory':
                kindResult = this.validateMemoryStep();
                break;
            case 'choose':
                kindResult = this.validateChooseStep(step as ChooseStep);
                break;
            case 'subflow':
                kindResult = this.validateSubflowStep(step as SubflowStep);
                break;
            case 'tool':
                kindResult = this.validateToolStep(step as ToolStep);
                break;
            case 'parallel':
                kindResult = this.validateParallelStep(step as ParallelStep);
                break;
            case 'call':
                kindResult = this.validateCallStep(step as CallStep);
                break;
            default:
                kindResult = {
 isValid: false,
message: `Unknown step kind: ${(step as any).kind}`
};
        }

        const errors = [
            ...commonErrors,
            ...(kindResult.isValid ? [] : [kindResult.message])
        ].filter(Boolean);

        return {
            isValid: errors.length === 0,
            message: errors.join(', ')
        };
    }

    private validateStepCommonFields(step: JsonOpcodeStep): string[] {
        const errors: string[] = [];
        const roles = new Set(['data-gather', 'reason', 'decide', 'act', 'notify']);
        if (step.role && !roles.has(step.role)) {
            errors.push(`role "${step.role}" is invalid (use data-gather, reason, decide, act, or notify)`);
        }
        if (step.timeout_ms != null) {
            if (!Number.isInteger(step.timeout_ms) || step.timeout_ms < 100 || step.timeout_ms > 600000) {
                errors.push('timeout_ms must be an integer between 100 and 600000');
            }
        }
        const onError = step.on_error;
        if (onError && typeof onError === 'object' && onError.action === 'fallback') {
            if (!onError.fallback_step_id?.trim()) {
                errors.push('fallback_step_id is required when on_error action is fallback');
            }
        }
        return errors;
    }

    private validateLLMStep(step: LLMStep): ValidationResult {
        const errors: string[] = [];
        if (!step.unique_name) errors.push('instruction is required');
        if (!step.model_type) errors.push('model type is required');
        if (!step.inputs?.query) errors.push('query is required');

        const outputType = step.output_type || 'string';
        const allowed = new Set(['string', 'dict', 'list', 'number']);
        if (!allowed.has(outputType)) {
            errors.push(`output_type "${outputType}" is invalid (use string, dict, list, or number)`);
        }
        if (step.stream && outputType !== 'string') {
            errors.push('stream output requires string output type (turn off stream or use String)');
        }

        return {
            isValid: errors.length === 0,
            message: errors.join(', ')
        };
    }

    private validateRAGStep(step: RAGStep): ValidationResult {
        const errors: string[] = [];
        if (!step.operation) errors.push('operation is required');
        if (!step.inputs) {
            errors.push('inputs are required');
        } else {
            if (!('collection_id' in step.inputs) || !step.inputs.collection_id) {
                errors.push('collection_id is required');
            }
            if (step.operation !== 'retrieve_files') {
                if (!('query' in step.inputs) || !step.inputs.query) {
                    errors.push('query is required');
                }
                if (!('top_k' in step.inputs) || !step.inputs.top_k) {
                    errors.push('top_k is required');
                }
            }
            if (step.operation === 'search_with_files' || step.operation === 'retrieve_files') {
                if (!('file_keys' in step.inputs) || !step.inputs.file_keys) {
                    errors.push('file_keys is required');
                }
            }
            if (step.operation === 'search_with_files') {
                if (!('top_n' in step.inputs) || step.inputs.top_n === '' || step.inputs.top_n == null) {
                    errors.push('top_n is required for search_with_files');
                }
            }
        }
        return {
            isValid: errors.length === 0,
            message: errors.join(', ')
        };
    }

    private validateAPIStep(step: APIStep): ValidationResult {
        if (!step.api_id) {
            return {
 isValid: false,
message: 'API selection is required'
};
        }
        return {
 isValid: true,
message: ''
};
    }

    private validateMemoryStep(): ValidationResult {
        // Memory step has sensible defaults, always valid
        return {
 isValid: true,
message: ''
};
    }

    private validateChooseStep(step: ChooseStep): ValidationResult {
        const errors: string[] = [];
        if (!step.choose?.cases || step.choose.cases.length === 0) {
            errors.push('at least one case is required');
        } else {
            step.choose.cases.forEach((c, i) => {
                if (!c.when) errors.push(`case ${i + 1}: condition (when) is required`);
                if (!c.run?.ref) errors.push(`case ${i + 1}: run reference is required`);
            });
        }
        return {
            isValid: errors.length === 0,
            message: errors.join(', ')
        };
    }

    private validateSubflowStep(step: SubflowStep): ValidationResult {
        if (!step.subflow?.ref) {
            return {
 isValid: false,
message: 'subflow reference is required'
};
        }
        return {
 isValid: true,
message: ''
};
    }

    // Parallel — branches are full step definitions (any kind, including
    // nested parallel). Require ≥1 branch and validate each recursively so a
    // bad nested step is reported instead of silently saving.
    private validateParallelStep(step: ParallelStep): ValidationResult {
        const errors: string[] = [];
        if (!Array.isArray(step.branches) || step.branches.length === 0) {
            errors.push('at least one branch is required');
        } else {
            step.branches.forEach((branch, i) => {
                const result = this.validateStep(branch);
                if (!result.isValid) {
                    errors.push(`branch ${i + 1} (${branch.id}): ${result.message}`);
                }
            });
        }
        return {
            isValid: errors.length === 0,
            message: errors.join(', ')
        };
    }

    // Call — invokes a definition by ref.
    private validateCallStep(step: CallStep): ValidationResult {
        if (!step.call?.ref) {
            return {
 isValid: false,
message: 'call reference (call.ref) is required'
};
        }
        return {
 isValid: true,
message: ''
};
    }

    // A-0008.06 — tool step validator. Protocol-conditional per Option 1
    // refactor (2026-04-26): opcode_as_tool requires only tool_name (no
    // configurable access boundary), mcp requires both connection_id and
    // tool_name. Mirrors the Server-side Joi alternatives switch.
    private validateToolStep(step: ToolStep): ValidationResult {
        if (!step.protocol) {
            return {
 isValid: false,
message: 'protocol is required'
};
        }
        if (!step.tool_ref) {
            return {
 isValid: false,
message: 'tool_ref is required'
};
        }
        if (!step.tool_ref.tool_name) {
            return {
 isValid: false,
message: 'tool_ref.tool_name is required'
};
        }
        if (step.protocol === 'mcp') {
            const refAsMcp = step.tool_ref as { connection_id?: string };
            if (!refAsMcp.connection_id) {
                return {
 isValid: false,
message: 'tool_ref.connection_id is required for protocol=mcp'
};
            }
        }
        if (step.protocol === 'opcode_as_tool') {
            const refAsAny = step.tool_ref as { connection_id?: string };
            if (refAsAny.connection_id) {
                return {
 isValid: false,
message: 'tool_ref.connection_id is forbidden for protocol=opcode_as_tool (no configurable access boundary)'
};
            }
        }
        return {
 isValid: true,
message: ''
};
    }

    validateReferences(opcode: OpcodeJsonShape): ValidationResult {
        const definitionIds = new Set((opcode.definitions || []).map(d => d.id));
        const flowIds = new Set((opcode.flows || []).map(f => f.id));
        const errors: string[] = [];

        const checkRefs = (steps: JsonOpcodeStep[], context: string) => {
            steps.forEach(step => {
                if (step.kind === 'choose') {
                    const chooseStep = step as ChooseStep;
                    const allRuns = [
                        ...chooseStep.choose.cases.map(c => c.run),
                        ...(chooseStep.choose.default ? [chooseStep.choose.default] : [])
                    ];
                    allRuns.forEach(run => {
                        if (run.kind === 'step' && run.ref && !definitionIds.has(run.ref)) {
                            errors.push(`${context} step "${step.id}": references definition "${run.ref}" which does not exist`);
                        }
                        if (run.kind === 'flow' && run.ref && !flowIds.has(run.ref)) {
                            errors.push(`${context} step "${step.id}": references flow "${run.ref}" which does not exist`);
                        }
                    });
                }
                if (step.kind === 'subflow') {
                    const subflowStep = step as SubflowStep;
                    if (subflowStep.subflow?.ref && !flowIds.has(subflowStep.subflow.ref)) {
                        errors.push(`${context} step "${step.id}": references flow "${subflowStep.subflow.ref}" which does not exist`);
                    }
                }
            });
        };

        checkRefs(opcode.steps, 'Main');
        (opcode.flows || []).forEach(flow => {
            checkRefs(flow.steps, `Flow "${flow.id}"`);
        });

        return {
            isValid: errors.length === 0,
            message: errors.join('. ')
        };
    }

    validateFallbackStepRefs(opcode: OpcodeJsonShape): ValidationResult {
        const stepIds = new Set<string>();
        const visit = (s: JsonOpcodeStep) => {
            if (s?.id) stepIds.add(s.id);
            if (s.kind === 'parallel') {
                (s as ParallelStep).branches?.forEach(visit);
            }
        };
        opcode.steps.forEach(visit);
        (opcode.definitions || []).forEach(visit);
        (opcode.flows || []).forEach((f) => (f.steps || []).forEach(visit));

        const errors: string[] = [];
        const check = (steps: JsonOpcodeStep[], context: string) => {
            steps.forEach((step) => {
                const onError = step.on_error;
                if (onError && typeof onError === 'object' && onError.action === 'fallback') {
                    const fid = onError.fallback_step_id;
                    if (fid && !stepIds.has(fid)) {
                        errors.push(`${context} step "${step.id}": fallback_step_id "${fid}" not found`);
                    }
                }
                if (step.kind === 'parallel') {
                    (step as ParallelStep).branches?.forEach((b) => check([b], context));
                }
            });
        };
        check(opcode.steps, 'Main');
        (opcode.definitions || []).forEach((d) => check([d], 'Definition'));
        (opcode.flows || []).forEach((f) => check(f.steps || [], `Flow "${f.id}"`));

        return {
            isValid: errors.length === 0,
            message: errors.join('. ')
        };
    }

    // Cross-checks external resource references against registries the caller
    // resolved (typically from V2OpCodeBuilderContext). Literal ids are
    // checked; OEL expressions are ignored (cannot resolve statically).
    validateExternalReferences(opcode: OpcodeJsonShape, refs: AvailableRefs): ValidationResult {
        const errors: string[] = [];

        const checkStep = (step: JsonOpcodeStep, context: string) => {
            if (step.kind === 'llm' && refs.instructionUniqueNames) {
                const name = (step as LLMStep).unique_name;
                if (name && !isOelExpr(name) && !refs.instructionUniqueNames.has(name)) {
                    errors.push(`${context} step "${step.id}": instruction "${name}" not found`);
                }
            }
            if (step.kind === 'api' && refs.apiIds) {
                const apiId = (step as APIStep).api_id;
                if (apiId && !isOelExpr(apiId) && !refs.apiIds.has(apiId)) {
                    errors.push(`${context} step "${step.id}": API "${apiId}" not found`);
                }
            }
            if (step.kind === 'rag' && refs.knowledgeCollectionIds) {
                const collectionId = (step as RAGStep).inputs?.collection_id;
                if (typeof collectionId === 'string' && collectionId && !isOelExpr(collectionId)
                    && !refs.knowledgeCollectionIds.has(collectionId)) {
                    errors.push(`${context} step "${step.id}": knowledge collection "${collectionId}" not found`);
                }
            }
            if (step.kind === 'tool') {
                const toolStep = step as ToolStep;
                if (toolStep.protocol === 'mcp' && refs.mcpConnectionIds) {
                    const connId = (toolStep.tool_ref as { connection_id?: string })?.connection_id;
                    if (connId && !isOelExpr(connId) && !refs.mcpConnectionIds.has(connId)) {
                        errors.push(`${context} step "${step.id}": MCP connection "${connId}" not found`);
                    }
                }
            }
            if (step.kind === 'parallel') {
                (step as any).branches?.forEach((b: JsonOpcodeStep) => checkStep(b, context));
            }
        };

        opcode.steps.forEach(s => checkStep(s, 'Main'));
        (opcode.definitions || []).forEach(d => checkStep(d, 'Definition'));
        (opcode.flows || []).forEach(flow => {
            flow.steps.forEach(s => checkStep(s, `Flow "${flow.id}"`));
        });

        return {
            isValid: errors.length === 0,
            message: errors.join('. ')
        };
    }

    validateUniqueIds(opcode: OpcodeJsonShape): ValidationResult {
        const mainIds = opcode.steps.map(s => s.id);
        const defIds = (opcode.definitions || []).map(d => d.id);
        const flowIds = (opcode.flows || []).map(f => f.id);
        const errors: string[] = [];

        // Check main step IDs uniqueness
        const mainDupes = mainIds.filter((id, i) => mainIds.indexOf(id) !== i);
        if (mainDupes.length > 0) {
            errors.push(`Duplicate step IDs in main steps: ${Array.from(new Set(mainDupes)).join(', ')}`);
        }

        // Check definition IDs uniqueness
        const defDupes = defIds.filter((id, i) => defIds.indexOf(id) !== i);
        if (defDupes.length > 0) {
            errors.push(`Duplicate definition IDs: ${Array.from(new Set(defDupes)).join(', ')}`);
        }

        // Check flow IDs uniqueness
        const flowDupes = flowIds.filter((id, i) => flowIds.indexOf(id) !== i);
        if (flowDupes.length > 0) {
            errors.push(`Duplicate flow IDs: ${Array.from(new Set(flowDupes)).join(', ')}`);
        }

        return {
            isValid: errors.length === 0,
            message: errors.join('. ')
        };
    }

    /**
     * Collects all available step output references for OEL autocomplete.
     * Returns step IDs from preceding steps that can be referenced.
     */
    getPreviousStepIds(steps: JsonOpcodeStep[], currentIndex: number): string[] {
        return steps.slice(0, currentIndex).map(s => s.id);
    }
}

const v2Handler = new V2OpCodeBuilderHandler();
export default v2Handler;
